import Typography from '@mui/material/Typography'
import { alpha, styled } from '@mui/material/styles'

type ImmersiveProps = {
  immersive?: boolean
}

type LegendSwatchProps = {
  swatchcolor: string
}

export const StorageLayoutRoot = styled('section', {
  shouldForwardProp: (prop) => prop !== 'immersive',
})<ImmersiveProps>(({ immersive, theme }) => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
  padding: immersive ? theme.spacing(1.5) : theme.spacing(1.25),
  border: '1px solid rgba(173, 223, 255, 0.1)',
  background: immersive
    ? 'radial-gradient(circle at top left, rgba(86, 176, 226, 0.18) 0%, rgba(86, 176, 226, 0) 30%), linear-gradient(180deg, rgba(12, 18, 27, 0.92) 0%, rgba(10, 15, 22, 0.98) 100%)'
    : 'linear-gradient(180deg, rgba(12, 18, 27, 0.72) 0%, rgba(10, 15, 22, 0.92) 100%)',
  boxShadow:
    '0 18px 40px rgba(3, 8, 18, 0.32), inset 0 1px 0 rgba(255,255,255,0.04)',
  [theme.breakpoints.up('sm')]: {
    padding: immersive ? theme.spacing(2.5) : theme.spacing(1.5),
  },
}))

export const StorageLayoutHeader = styled('div', {
  shouldForwardProp: (prop) => prop !== 'immersive',
})<ImmersiveProps>(({ immersive, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  marginBottom: immersive ? theme.spacing(2) : theme.spacing(1.4),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
}))

export const StorageLayoutHeaderContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  maxWidth: 760,
}))

export const StorageLayoutEyebrow = styled(Typography)(() => ({
  color: 'rgba(180, 226, 255, 0.94)',
  fontSize: '0.7rem',
  fontWeight: 800,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
}))

export const StorageLayoutTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'immersive',
})<ImmersiveProps>(({ immersive, theme }) => ({
  color: '#f6fbff',
  fontSize: immersive ? '1.1rem' : '1rem',
  fontWeight: 700,
  lineHeight: 1.3,
  [theme.breakpoints.up('sm')]: {
    fontSize: immersive ? '1.35rem' : '1rem',
  },
}))

export const StorageLayoutLegend = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.8),
  justifyContent: 'flex-start',
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
  },
}))

export const StorageLayoutLegendItem = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.7),
  padding: theme.spacing(0.6, 1),
  border: '1px solid rgba(173, 223, 255, 0.12)',
  backgroundColor: alpha('#0a121c', 0.38),
  color: '#d8f3ff',
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
}))

export const StorageLayoutLegendSwatch = styled('span', {
  shouldForwardProp: (prop) => prop !== 'swatchcolor',
})<LegendSwatchProps>(({ swatchcolor }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: swatchcolor,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2)',
  flexShrink: 0,
}))

export const StorageLayoutGraphicShell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'immersive',
})<ImmersiveProps>(({ immersive, theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: immersive ? theme.spacing(0.6) : theme.spacing(0.55),
  borderRadius: immersive ? 24 : 16,
  background:
    'linear-gradient(180deg, rgba(227, 234, 240, 0.92) 0%, rgba(214, 223, 231, 0.92) 100%)',
  boxShadow:
    '0 22px 48px rgba(0, 0, 0, 0.24), inset 0 1px 0 rgba(255,255,255,0.5)',
  [theme.breakpoints.up('sm')]: {
    padding: immersive ? theme.spacing(0.9) : theme.spacing(0.55),
  },
}))

export const StorageLayoutGridOverlay = styled('div', {
  shouldForwardProp: (prop) => prop !== 'immersive',
})<ImmersiveProps>(({ immersive }) => ({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  opacity: 0.35,
  backgroundImage:
    'linear-gradient(rgba(102, 133, 157, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(102, 133, 157, 0.08) 1px, transparent 1px)',
  backgroundSize: immersive ? '34px 34px' : '28px 28px',
}))

export const StorageLayoutImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'immersive',
})<ImmersiveProps>(({ immersive, theme }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'block',
  width: '100%',
  objectFit: 'contain',
  filter: 'saturate(1.02) contrast(1.03)',
  maxHeight: immersive ? '100%' : 260,
  [theme.breakpoints.up('sm')]: {
    maxHeight: immersive ? '100%' : 320,
  },
  [theme.breakpoints.up('lg')]: {
    maxHeight: immersive ? '100%' : 360,
  },
}))

export const StorageLayoutEntranceTag = styled('div', {
  shouldForwardProp: (prop) => prop !== 'immersive',
})<ImmersiveProps>(({ immersive, theme }) => ({
  position: 'absolute',
  zIndex: 2,
  left: immersive ? 18 : 16,
  bottom: immersive ? 18 : 14,
  padding: theme.spacing(0.7, 1.15),
  border: '1px solid rgba(143, 205, 240, 0.2)',
  backgroundColor: 'rgba(9, 15, 22, 0.9)',
  color: '#d8f2ff',
  fontSize: '0.72rem',
  fontWeight: 800,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  [theme.breakpoints.up('sm')]: {
    left: immersive ? 24 : 16,
    bottom: immersive ? 24 : 14,
  },
}))
